<template>
  <div class="layout">
    <top-bar v-if="!customizeHeader"
             show-header
             :title="title"
             :fixed="isFixed"
             :show-back="showBack"
    />
    <div class="router-view"
         :style="routerViewStyle"
    >
      <keep-alive :max="10"
                  :include="keepAliveComponent"
      >
        <router-view />
      </keep-alive>
    </div>

    <ym-tabbar v-if="!isMiniWebView && $route.meta.tab"
               class="layout-tabbar"
               :class="{ 'ym-in-app': isAppWebView }"
               route
               safe-area-inset-bottom
    >
      <ym-tabbar-item v-for="item in tabBar"
                      :name="item.id"
                      :key="item.id"
                      :icon="$route.name === item.id ? item.selectedIcon : item.icon"
                      :to="{ name: item.id }"
      >
        {{ item.text }}
      </ym-tabbar-item>
    </ym-tabbar>

    <activity-popup v-if="activityInfo"
                    :activity-info="activityInfo"
    />

    <certificate-popup :id="certificate.id"
                       :title="certificate.product.title"
                       :show="showCertificate"
    />

    <player status="bar" />

    <product-wechat-group-info :show.sync="showWeChatGroup"
                               :product="wechatGroupProductInfo"
                               :user-info="wechatGroupUserInfo"
    />
    <common-coupon-popup :show.sync="showCommonCouponPopup"
                         :info="commonCouponPopupInfo"
    />
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { Tabbar, TabbarItem } from '@ymba/ym-ui';
  import ActivityPopup from '@/components/ActivityPopup';
  import CertificatePopup from '@/components/CertificatePopup';
  import TopBar from '@/components/TopBar';
  import Player from '@/components/Player';
  import ProductWechatGroupInfo from '@/components/ProductWechatGroupInfo';
  import CommonCouponPopup from '@/components/CommonCouponPopup';
  import {
    getActivityPopupInfo,
    commonLog,
    getProductWeChatGroupInfo,
    getCommonCouponPopupInfo,
  } from '@/services/app';
  import { showCertificatePopup } from '@/services/certificate';
  import { setProgramPlayEndLog } from '@/services/plan';
  import {
    getRouterViewHeight,
    getFromStorage,
    getUID,
    isAppWebView,
    isIosWebView,
    isMiniWebView,
  } from '@/utils';
  import { MULTI_WECHAT_GROUP_STORAGE_KEY_PREFIX } from '@/utils/constant';

  export default {
    name: 'Layout',
    components: {
      ActivityPopup,
      CertificatePopup,
      TopBar,
      Player,
      'ym-tabbar': Tabbar,
      'ym-tabbar-item': TabbarItem,
      ProductWechatGroupInfo,
      CommonCouponPopup,
    },
    data() {
      return {
        isAppWebView: isAppWebView(),
        isMiniWebView: isMiniWebView(),
        isIosWebView: isIosWebView(),
        activityInfo: null,
        certificate: {
          id: 0,
          product: {
            title: '',
          },
        },
        isFixed: false,
        keepAliveComponent: [
          'PageHome',
          'PageAudio',
          'PageCourse',
          'PageLearning',
          'PageMy',
        ],
        tabBar: [{
          id: 'Home',
          icon: 'home',
          selectedIcon: 'home-active',
          text: '首页',
        }, {
          id: 'Audio',
          icon: 'audio',
          selectedIcon: 'audio-active',
          text: '音频课',
        }, {
          id: 'Course',
          icon: 'live',
          selectedIcon: 'live-active',
          text: '直播课',
        }, {
          id: 'Learning',
          icon: 'study_default',
          selectedIcon: 'study_select',
          text: '学习',
        }, {
          id: 'Mine',
          icon: 'mine',
          selectedIcon: 'mine-active',
          text: '个人中心',
        }],
        downloadBarHeight: 0,
        showCertificate: false,
        showWeChatGroup: false,
        wechatGroupUserInfo: null,
        wechatGroupProductInfo: null,
        commonCouponPopupInfo: null,
        showCommonCouponPopup: false,
      };
    },
    computed: {
      ...mapGetters([
        'player',
        'customizeHeader',
        'title',
        'showBack',
      ]),
      routerViewStyle() {
        const style = {};
        style.height = this.getRouterViewHeight();
        return style;
      },
      redirectTo() {
        return this.$route.fullPath;
      },
    },
    watch: {
      $route() {
        // 当前页面是否需要展示活动Popup
        if (!this.isIosWebView) this.getActivityPopupInfo();
        // 当前页面是否需要展示入群二维码
        this.getProductWeChatGroupInfo();
        // App内默认先隐藏分享按钮
        this.resetAppShare();
        // 通用优惠券弹窗
        if (!this.isIosWebView) this.getCommonCouponPopupInfo();
      },
    },
    methods: {
      async getCommonCouponPopupInfo() {
        const data = await getCommonCouponPopupInfo(this.$route.name);
        this.commonCouponPopupInfo = data.info;
        this.showCommonCouponPopup = Boolean(data.info);
      },
      resetAppShare() {
        const inAppWebView = isAppWebView();
        const hasAppSetShareInfo = window.setShareInfo && typeof window.setShareInfo === 'function';
        if (inAppWebView && hasAppSetShareInfo) {
          window.setShareInfo(null);
        }
      },
      async getProductWeChatGroupInfo() {
        // 支付通知页不展示
        if (this.$route.name === 'PayNotify') return;

        const data = await getProductWeChatGroupInfo();
        if (data.product && data.product.length > 1) {
          // 判断是否已有跳过展示的缓存及对比数值
          const userId = getUID();
          const storageName = `${MULTI_WECHAT_GROUP_STORAGE_KEY_PREFIX}${userId}`;
          const value = getFromStorage(storageName);
          const now = new Date().getTime();
          if (value && now - parseInt(value, 10) < 0) return;

          const { fullPath } = this.$route;
          this.$router.push({ name: 'MultiProductWeChatGroup', query: { redirectTo: fullPath } });
          return;
        }
        if (data.product && data.product.length > 0) {
          this.wechatGroupUserInfo = data.userInfo;
          const product = data.product[0];
          this.wechatGroupProductInfo = product;
          setTimeout(() => {
            this.showWeChatGroup = Boolean(this.wechatGroupProductInfo);
          }, 300);
        }
      },
      getRouterViewHeight() {
        if (this.customizeHeader) return '100%';
        return getRouterViewHeight();
      },
      async getActivityPopupInfo() {
        const data = await getActivityPopupInfo();
        if (data != null) {
          const { activity } = data;
          this.activityInfo = activity;
        }
      },
      async showCertificatePopup(programId) {
        try {
          const data = await showCertificatePopup('program', programId);
          const { render = {} } = data;
          if (!render.show) return;
          this.certificate = data.certificate;
          this.certificate.product = data.product;
          this.showCertificate = true;
        } catch (e) {
          commonLog('showCertificatePopupError', e);
        }
      },
      async setProgramPlayEndLog(programId) {
        try {
          await setProgramPlayEndLog(programId);
        } catch (e) {
          commonLog('programPlayEndLogError', e);
        }
      },
      setupPlayerListener() {
        const lessonCompleteListener = this.player.on('lessonComplete', (params) => {
          const { id: programId } = params;
          // 展示获取证书逻辑
          this.showCertificatePopup(programId);
          // 处理学习计划学完记录
          this.setProgramPlayEndLog(programId);
        });
        this.$once('hook:beforeDestroy', () => {
          this.player.off(lessonCompleteListener);
        });
      },
    },
    created() {
      // 监听播放器
      this.setupPlayerListener();
    },
  };
</script>

<style scoped lang="less">
  @import '~@/assets/styles/var.less';

  .layout {
    height: 100vh;
    height: 100%;
  }

  .router-view {
    position: relative;
    box-sizing: border-box;
    max-width: 100%;
  }
</style>
